// adminfrontend/src/admin_ui/pages/AdminComplianceDashboardPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminComplianceApi from "../services/adminComplianceApi";
import "../styles/Admin.css";

function asArr(v) {
  return Array.isArray(v) ? v : [];
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function AdminComplianceDashboardPage({ adminEmail, onLogout }) {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const verBy = useMemo(() => {
    const v = data?.verifications?.byResult || data?.verifications_by_result || data?.byResult || data?.verifications || [];
    return asArr(v);
  }, [data]);

  const riskBy = useMemo(() => {
    const v = data?.risk?.bySeverity || data?.risk_by_severity || data?.bySeverity || data?.risk || [];
    return asArr(v);
  }, [data]);

  const shipBy = useMemo(() => {
    const v = data?.shipments?.byStatus || data?.shipments_by_status || data?.byStatus || data?.shipments || [];
    return asArr(v);
  }, [data]);

  const pendingMatrix = useMemo(() => {
    const v =
      data?.shipments?.pendingMatrix ||
      data?.pending_matrix ||
      data?.pendingMatrix ||
      [];
    return asArr(v);
  }, [data]);

  const totals = useMemo(() => {
    return {
      verifications: safeNum(data?.verifications?.total ?? data?.verifications_total ?? data?.totalVerifications),
      risk: safeNum(data?.risk?.total ?? data?.risk_total ?? data?.totalRiskEvents),
      shipments: safeNum(data?.shipments?.total ?? data?.shipments_total ?? data?.totalShipments),
    };
  }, [data]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await adminComplianceApi.overview({ from, to });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      setData(res);
    } catch (e) {
      console.error("[AdminComplianceDashboardPage] load error:", e);
      setData(null);
      setError("Unable to load compliance overview. Check server or login.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Compliance — Overview</h1>
            <p className="ucx-admin-subtitle">Aggregated compliance signals for verification, risk, and logistics.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Range</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <label className="ucx-small">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <label className="ucx-small">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <button type="button" className="ucx-btn ucx-btn-primary" onClick={load} disabled={loading}>
              Apply
            </button>
          </div>

          {error && <div className="ucx-admin-status-error">{error}</div>}
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Totals</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <div className="ucx-admin-toolbar-hint">Verifications: <b>{totals.verifications}</b></div>
            <div className="ucx-admin-toolbar-hint">Risk events: <b>{totals.risk}</b></div>
            <div className="ucx-admin-toolbar-hint">Shipments: <b>{totals.shipments}</b></div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Verifications by result</div>
          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>Result</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={2} className="ucx-admin-empty">Loading…</td></tr>
                )}
                {!loading && verBy.length === 0 && (
                  <tr><td colSpan={2} className="ucx-admin-empty">No data.</td></tr>
                )}
                {verBy.map((r, i) => (
                  <tr key={r.result || r.status || i}>
                    <td>{r.result ?? r.status ?? r.label ?? "-"}</td>
                    <td>{r.count ?? r.total ?? r.n ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Risk events by severity</div>
          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={2} className="ucx-admin-empty">Loading…</td></tr>
                )}
                {!loading && riskBy.length === 0 && (
                  <tr><td colSpan={2} className="ucx-admin-empty">No data.</td></tr>
                )}
                {riskBy.map((r, i) => (
                  <tr key={r.severity || r.level || i}>
                    <td>{r.severity ?? r.level ?? r.label ?? "-"}</td>
                    <td>{r.count ?? r.total ?? r.n ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Shipments by status</div>
          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={2} className="ucx-admin-empty">Loading…</td></tr>
                )}
                {!loading && shipBy.length === 0 && (
                  <tr><td colSpan={2} className="ucx-admin-empty">No data.</td></tr>
                )}
                {shipBy.map((r, i) => (
                  <tr key={r.status || i}>
                    <td>{r.status ?? r.label ?? "-"}</td>
                    <td>{r.count ?? r.total ?? r.n ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Shipments pending matrix</div>
          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>Pending status</th>
                  <th>Pending action</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={3} className="ucx-admin-empty">Loading…</td></tr>
                )}
                {!loading && pendingMatrix.length === 0 && (
                  <tr><td colSpan={3} className="ucx-admin-empty">No pending data.</td></tr>
                )}
                {pendingMatrix.map((r, i) => (
                  <tr key={`${r.pending_status || r.pendingStatus || "x"}-${i}`}>
                    <td>{r.pending_status ?? r.pendingStatus ?? "-"}</td>
                    <td>{r.pending_action ?? r.pendingAction ?? "-"}</td>
                    <td>{r.count ?? r.total ?? r.n ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && data && (
            <div className="ucx-small" style={{ marginTop: 10 }}>
              If some tables are blank, ensure your compliance.service returns group breakdown arrays (byResult/bySeverity/byStatus).
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
